'use client'

import { Box, Center, Circle, Float, HStack, Text } from '@chakra-ui/react'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'
import styles from './styles.module.css'

import { Link, usePathname } from '@/i18n/navigation'
import { bottomNavigationItems } from '../model/constants'
import { useCartStore } from '@/entities/cart/model/use-cart-store'

const BottomNavigation = ({ isTgBot = false }: { isTgBot?: boolean }) => {
  const [activeOrderCount, setActiveOrderCount] = useState(0)
  const [optimisticIndex, setOptimisticIndex] = useState<number | null>(null)

  const pathname = usePathname()
  const t = useTranslations('bottom-nav')
  const cart = useCartStore((s) => s.cart)

  // Derive active index from current pathname
  const pathnameBasedIndex = bottomNavigationItems.findIndex((item) => {
    if (item.href === '/') return pathname === '/'
    return pathname.startsWith(item.href)
  })

  // Use optimistic index for instant feedback, fall back to pathname-based
  const activeIndex =
    optimisticIndex !== null ? optimisticIndex : pathnameBasedIndex

  // Sync optimistic state with actual pathname changes
  useEffect(() => {
    setOptimisticIndex(null)
  }, [pathname])

  return (
    <>
      <nav className={styles.nav}>
        <div className={styles.waveWrap}>
          <svg
            version="1.1"
            id="wave"
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
            viewBox="0 0 119 26"
            style={{
              width: isTgBot ? 'calc(100vw / 2)' : 'calc(100vw / 4)',
              left: `${activeIndex * 25}%`,
            }}
          >
            <path
              className={styles.path}
              d="M120.8,26C98.1,26,86.4,0,60.4,0C35.9,0,21.1,26,0.5,26H120.8z"
            />
          </svg>
        </div>
        <HStack
          roundedTop="15px"
          bgColor="background.card"
          height="70px"
          gap={0}
          boxShadow="0px -8px 30px 0px #00000014"
        >
          <Center
            zIndex={1}
            position="absolute"
            width={isTgBot ? 'calc(100vw / 2)' : 'calc(100vw / 4)'}
            transition="all 0.6s cubic-bezier(0.23, 1, 0.32, 1)"
            top="23px"
            left={`${activeIndex * 25}vw`}
          >
            <Box
              minHeight="40px"
              minWidth="40px"
              rounded="full"
              bgColor="primary"
            ></Box>
          </Center>
          {bottomNavigationItems.map((item, index) => {
            const isActive = index === activeIndex

            return (
              <Center
                key={item.title + index}
                flex={1}
                fontSize="xl"
                flexDir="column"
                zIndex={1}
                transform={`translateY(${isActive ? '-15' : '0'}px)`}
                transition="all 0.6s cubic-bezier(0.23, 1, 0.32, 1)"
              >
                <Link
                  href={item.href}
                  onClick={() => setOptimisticIndex(index)}
                  prefetch={true}
                >
                  <Box position="relative">
                    {item.title === 'cart' && cart?.length > 0 && (
                      <Float top={0} right={0}>
                        <Circle
                          size="4"
                          bg="primary"
                          fontSize="9px"
                          color="white"
                        >
                          {cart.length}
                        </Circle>
                      </Float>
                    )}
                    {item.title === 'orders' && activeOrderCount > 0 && (
                      <Float top={0} right={3}>
                        <Circle
                          size="4"
                          bg={'primary'}
                          fontSize="9px"
                          color={'white'}
                        >
                          {activeOrderCount}
                        </Circle>
                      </Float>
                    )}
                    <Center
                      minHeight={isActive ? '40px' : '24px'}
                      minWidth={isActive ? '40px' : '24px'}
                      rounded="full"
                      transition="all 0.6s cubic-bezier(0.23, 1, 0.32, 1)"
                    >
                      {item.icon(isActive ? 'white' : 'var(--text-muted)')}
                    </Center>
                    <Text
                      textAlign="center"
                      fontSize="2xs"
                      color={isActive ? 'primary' : 'text.muted'}
                    >
                      {t(item.title)}
                    </Text>
                  </Box>
                </Link>
              </Center>
            )
          })}
        </HStack>
      </nav>
    </>
  )
}

export default BottomNavigation
